import { useState, Fragment } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AlertCircle, RefreshCw, XCircle, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApartments } from '../hooks/useApartments';
import { bookingQueryOptions, refetchBookingQueries, useLinkBooking } from '../hooks/useBookings';
import { useSupportCaseList } from '../hooks/useSupportCases';
import { BookingStatusBadge } from '../components/BookingStatusBadge';
import { BookingSourceBadge } from '../components/BookingSourceBadge';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { CancelBookingDialog } from '../components/CancelBookingDialog';
import { ScheduledTimesDisplay } from '../components/ScheduledTimesDisplay';
import { CheckInOutActions } from '../components/CheckInOutActions';
import { AdjustTimeForm } from '../components/AdjustTimeForm';
import { ListRefreshButton } from '../components/ListRefreshButton';
import { OpenSupportCaseForm } from '../components/OpenSupportCaseForm';
import { SupportCaseIndicator } from '../components/SupportCaseIndicator';
import { Booking } from '../types/booking';
import type { KnownBookingSource } from '../types/booking';
import type { PaymentStatus } from '../types/payment';
import { derivePaymentAmounts } from '../utils/paymentAmounts';
import { formatDate } from '../utils/formatDate';
import { isAuthRequiredError } from '../utils/listRefreshState';
import { NON_FINAL_SUPPORT_CASE_STATUSES } from '../types/supportCase';

const Bookings = () => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inquiryId = searchParams.get('inquiryId');

  const [apartmentFilter, setApartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null);
  // ID of the expanded booking row
  const [expandedBookingId, setExpandedBookingId] = useState<string | null>(null);
  // Which time field is being adjusted: 'checkIn' | 'checkOut' | null
  const [adjustingField, setAdjustingField] = useState<'checkIn' | 'checkOut' | null>(null);
  // Toast/banner message for check-in/out and time-adjust feedback
  const [feedbackMessage, setFeedbackMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  // Support case opening per booking: bookingId → { open: bool, error: string|null }
  const [caseFormState, setCaseFormState] = useState<Record<string, { open: boolean; error: string | null }>>({});
  // Manual booking link state per booking
  const [linkState, setLinkState] = useState<Record<string, { success: boolean; error: string | null }>>({});

  const { data: apartments = [] } = useApartments();
  const linkBooking = useLinkBooking();

  // Fetch all non-final support cases to join with booking rows
  const { data: nonFinalCasesPage } = useSupportCaseList({
    statuses: [...NON_FINAL_SUPPORT_CASE_STATUSES],
    sort: 'openedAt,desc',
  });
  const nonFinalCasesByBookingId = new Map(
    (nonFinalCasesPage?.content ?? []).map(c => [c.bookingId, c]),
  );

  const bookingQueries = useQueries({
    queries: apartments.map(apt => bookingQueryOptions(apt.id)),
  });

  const isLoading = bookingQueries.some(q => q.isLoading);
  const isError   = bookingQueries.some(q => q.isError);
  const isFetching = bookingQueries.some(q => q.isFetching);
  const allBookings = bookingQueries.flatMap(q => q.data ?? []);

  const handleRetry = () => bookingQueries.forEach(q => q.refetch());

  const handleRefresh = async () => {
    try {
      setRefreshError(null);
      await refetchBookingQueries(bookingQueries);
    } catch (error) {
      setRefreshError(isAuthRequiredError(error) ? t('common:signInRequired') : t('common:refreshFailed'));
    }
  };

  const availableStatuses = [...new Set(allBookings.map(b => b.status))].sort();

  const PAYMENT_STATUS_OPTIONS: ReadonlyArray<{ value: PaymentStatus; label: string }> = [
    { value: 'SUCCEEDED', label: t('badges:statusPaid') },
    { value: 'INITIATED', label: t('badges:statusPending') },
    { value: 'FAILED',    label: t('badges:statusFailed') },
    { value: 'EXPIRED',   label: t('badges:statusExpired') },
  ];

  const SOURCE_OPTIONS: ReadonlyArray<{ value: KnownBookingSource; label: string }> = [
    { value: 'DIRECT', label: t('badges:sourceDirect') },
    { value: 'AIRBNB', label: t('badges:sourceAirbnb') },
  ];

  const filtered = allBookings
    .filter(b => !apartmentFilter       || b.apartmentId      === apartmentFilter)
    .filter(b => !statusFilter          || b.status           === statusFilter)
    .filter(b => !paymentStatusFilter   || b.payment?.status  === paymentStatusFilter)
    .filter(b => !sourceFilter          || (b.source ?? 'DIRECT') === sourceFilter)
    .sort((a, b) => b.checkInDate.localeCompare(a.checkInDate));

  const getApartmentName = (id: string) =>
    apartments.find(a => a.id === id)?.name ?? id;

  const toggleExpanded = (bookingId: string) => {
    if (expandedBookingId === bookingId) {
      setExpandedBookingId(null);
      setAdjustingField(null);
    } else {
      setExpandedBookingId(bookingId);
      setAdjustingField(null);
    }
  };

  const handleFeedbackSuccess = (text: string) => {
    setFeedbackMessage({ text, type: 'success' });
    setTimeout(() => setFeedbackMessage(null), 4000);
  };

  const handleFeedbackError = (text: string) => {
    setFeedbackMessage({ text, type: 'error' });
    setTimeout(() => setFeedbackMessage(null), 6000);
  };

  // ── Empty: no apartments ───────────────────────────────────────────────
  if (!isLoading && apartments.length === 0) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('pages:bookingsHeading')}</h1>
          <p className="text-gray-600 mt-2">{t('pages:bookingsSubHeading')}</p>
        </div>
        <div className="card">
          <p className="text-gray-600 text-center py-12">
            {t('pages:noApartmentsForBookings')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:bookingsHeading')}</h1>
        <p className="text-gray-600 mt-2">{t('pages:bookingsSubHeading')}</p>
      </div>

      <div className="flex justify-end mb-4">
        <ListRefreshButton isRefreshing={isFetching} onRefresh={handleRefresh} />
      </div>

      {refreshError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{refreshError}</span>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Error banner ──────────────────────────────────────────────── */}
      {isError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{t('errors:failedToLoadBookings')}</span>
          <button
            onClick={handleRetry}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Feedback banner (check-in/out, time adjust) ──────────────── */}
      {feedbackMessage && (
        <div
          role="status"
          className={`mb-6 flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${
            feedbackMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {feedbackMessage.text}
          <button
            onClick={() => setFeedbackMessage(null)}
            className="ml-auto text-current opacity-60 hover:opacity-100"
            aria-label={t('common:close')}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Filters ───────────────────────────────────────────────────── */}
      {!isLoading && apartments.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={apartmentFilter}
            onChange={e => setApartmentFilter(e.target.value)}
            className="filter-control"
            aria-label={t('common:allApartments')}
          >
            <option value="">{t('common:allApartments')}</option>
            {apartments.map(apt => (
              <option key={apt.id} value={apt.id}>{apt.name}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="filter-control"
            aria-label={t('common:allStatuses')}
          >
            <option value="">{t('common:allStatuses')}</option>
            {availableStatuses.map(s => (
              <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ')}</option>
            ))}
          </select>

          <select
            value={paymentStatusFilter}
            onChange={e => setPaymentStatusFilter(e.target.value)}
            className="filter-control"
            aria-label={t('common:allPaymentStatuses')}
          >
            <option value="">{t('common:allPaymentStatuses')}</option>
            {PAYMENT_STATUS_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value)}
            className="filter-control"
            aria-label={t('common:allSources')}
          >
            <option value="">{t('common:allSources')}</option>
            {SOURCE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {(apartmentFilter || statusFilter || paymentStatusFilter || sourceFilter) && (
            <button
              onClick={() => { setApartmentFilter(''); setStatusFilter(''); setPaymentStatusFilter(''); setSourceFilter(''); }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <XCircle size={14} />
              {t('common:clearFilters')}
            </button>
          )}
        </div>
      )}

      {/* ── Loading ───────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="card flex justify-center py-16">
          <span className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────── */}
      {!isLoading && (
        <>
          {filtered.length === 0 ? (
            <div className="card">
              <p className="text-gray-600 text-center py-12">
                {allBookings.length === 0
                  ? t('pages:noBookingsYet')
                  : t('pages:noBookingsMatchFilters')}
              </p>
              {inquiryId && (
                <div className="text-center pb-6">
                  <Link
                    to={`/inquiries?inquiryId=${inquiryId}`}
                    className="text-sm text-primary-600 hover:underline"
                  >
                    {t('pages:backToInquiry', 'Back to inquiry')}
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnApartment')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnGuest')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnEmail')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnCheckIn')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnCheckOut')}</th>
                    <th className="pb-3 pr-4 font-medium text-right">{t('pages:columnNights')}</th>
                    <th className="pb-3 pr-4 font-medium text-right">{t('pages:columnTotal')}</th>
                     <th className="pb-3 pr-4 font-medium">{t('pages:columnStatus')}</th>
                     <th className="pb-3 pr-4 font-medium">{t('pages:fieldSource')}</th>
                     <th className="pb-3 pr-4 font-medium">{t('pages:columnPayment')}</th>
                     <th className="pb-3 pr-4 font-medium text-right">{t('pages:columnPaid')}</th>
                     <th className="pb-3 pr-4 font-medium text-right">{t('pages:columnPending')}</th>
                     <th className="pb-3 font-medium">{t('pages:columnActions')}</th>
                     <th className="pb-3 w-8" />
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-100">
                    {filtered.map(booking => (
                      <Fragment key={booking.bookingId}>
                    <tr key={booking.bookingId} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4 font-medium text-gray-900">
                        {getApartmentName(booking.apartmentId)}
                      </td>
                      <td className="py-3 pr-4 text-gray-700">
                        {booking.guestInfo.fullName}
                      </td>
                      <td className="py-3 pr-4 text-gray-600">
                        {booking.guestInfo.email}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                        {formatDate(booking.checkInDate)}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                        {formatDate(booking.checkOutDate)}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 text-right">
                        {booking.nights}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 text-right font-medium">
                        €{booking.totalPrice}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <BookingStatusBadge status={booking.status} />
                          {(() => {
                            const activeCase = nonFinalCasesByBookingId.get(booking.bookingId);
                            return activeCase
                              ? <SupportCaseIndicator caseId={activeCase.caseId} status={activeCase.status} />
                              : null;
                          })()}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <BookingSourceBadge source={booking.source ?? 'DIRECT'} />
                      </td>
                      <td className="py-3 pr-4">
                        {booking.payment
                          ? <PaymentStatusBadge status={booking.payment.status} />
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 text-right font-medium">
                        {(() => {
                          const { amountPaid } = derivePaymentAmounts(booking);
                          return amountPaid !== undefined ? `€${amountPaid}` : <span className="text-gray-400">—</span>;
                        })()}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 text-right font-medium">
                        {(() => {
                          const { amountPending } = derivePaymentAmounts(booking);
                          return amountPending !== undefined ? `€${amountPending}` : <span className="text-gray-400">—</span>;
                        })()}
                      </td>
                      <td className="py-3">
                        {booking.status !== 'CANCELLED' && (
                          <button
                            onClick={() => setCancelTarget(booking)}
                            className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors px-2 py-1 rounded hover:bg-red-50"
                          >
                            {t('pages:cancelAction')}
                          </button>
                        )}
                      </td>

                      {/* Expand/collapse chevron */}
                      <td className="py-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(booking.bookingId)}
                          aria-expanded={expandedBookingId === booking.bookingId}
                          aria-label={expandedBookingId === booking.bookingId ? t('common:collapse') : t('common:expand')}
                          className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                        >
                          {expandedBookingId === booking.bookingId
                            ? <ChevronUp size={16} />
                            : <ChevronDown size={16} />}
                        </button>
                      </td>
                    </tr>

                    {/* ── Detail expansion row ───────────────────────── */}
                    {expandedBookingId === booking.bookingId && (
                      <tr key={`${booking.bookingId}-detail`}>
                        <td colSpan={14} className="px-4 pb-4 bg-gray-50 border-b border-gray-100">
                          <div className="space-y-4 pt-3">
                            {/* Scheduled times */}
                            <ScheduledTimesDisplay booking={booking} />

                            {/* Check-in / check-out action buttons */}
                            <CheckInOutActions
                              booking={booking}
                              onSuccess={handleFeedbackSuccess}
                              onError={handleFeedbackError}
                            />

                            {/* Adjust-time forms */}
                            {(booking.scheduledCheckInTime || booking.scheduledCheckOutTime) && (
                              <div className="flex flex-wrap gap-3 text-xs">
                                {booking.scheduledCheckInTime && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAdjustingField(
                                        adjustingField === 'checkIn' ? null : 'checkIn'
                                      )
                                    }
                                    className="inline-flex items-center gap-1 text-gray-500 hover:text-primary-600 transition-colors"
                                  >
                                    <Clock size={13} />
                                    {t('pages:adjustCheckInTime')}
                                  </button>
                                )}
                                {booking.scheduledCheckOutTime && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAdjustingField(
                                        adjustingField === 'checkOut' ? null : 'checkOut'
                                      )
                                    }
                                    className="inline-flex items-center gap-1 text-gray-500 hover:text-primary-600 transition-colors"
                                  >
                                    <Clock size={13} />
                                    {t('pages:adjustCheckOutTime')}
                                  </button>
                                )}
                              </div>
                            )}

                            {adjustingField && (
                              <AdjustTimeForm
                                field={adjustingField}
                                booking={booking}
                                onSuccess={(msg) => {
                                  setAdjustingField(null);
                                  handleFeedbackSuccess(msg);
                                }}
                                onError={handleFeedbackError}
                                onClose={() => setAdjustingField(null)}
                              />
                            )}

                            {/* ── Manual booking link ───────────────── */}
                            {(() => {
                              const ls = linkState[booking.bookingId];
                              if (ls?.success) {
                                return (
                                  <p className="text-sm text-green-700">
                                    {t('pages:linkBookingSuccess')}
                                  </p>
                                );
                              }
                              return (
                                <div className="flex flex-col gap-1">
                                  <button
                                    type="button"
                                    disabled={linkBooking.isPending}
                                    onClick={() => {
                                      setLinkState(prev => ({
                                        ...prev,
                                        [booking.bookingId]: { success: false, error: null },
                                      }));
                                      linkBooking.mutate(
                                        { bookingId: booking.bookingId, email: booking.guestInfo.email },
                                        {
                                          onSuccess: () =>
                                            setLinkState(prev => ({
                                              ...prev,
                                              [booking.bookingId]: { success: true, error: null },
                                            })),
                                          onError: (err) => {
                                            const response = (err as { response?: { status: number; data?: { detail?: string; title?: string } } }).response;
                                            const status = response?.status;
                                            const detail = `${response?.data?.detail ?? ''} ${response?.data?.title ?? ''}`.toLowerCase();
                                            let msg: string;
                                            if (status === 409) {
                                              msg = t('errors:bookingAlreadyLinked');
                                            } else if (status === 404 && detail.includes('account')) {
                                              msg = t('errors:noAccountForGuestEmail');
                                            } else if (status === 404) {
                                              msg = t('errors:linkBookingNotFound');
                                            } else {
                                              msg = t('errors:failedToLinkBooking');
                                            }
                                            setLinkState(prev => ({
                                              ...prev,
                                              [booking.bookingId]: { success: false, error: msg },
                                            }));
                                          },
                                        },
                                      );
                                    }}
                                    className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline disabled:opacity-50"
                                  >
                                    {linkBooking.isPending
                                      ? t('pages:linkBookingLinking')
                                      : t('pages:linkBookingButton')}
                                  </button>
                                  {ls?.error && (
                                    <p className="text-xs text-red-600">{ls.error}</p>
                                  )}
                                </div>
                              );
                            })()}

                            {/* ── Support Case ─────────────────────── */}
                            {(() => {
                              const existingCase = nonFinalCasesByBookingId.get(booking.bookingId);
                              const state = caseFormState[booking.bookingId];

                              if (existingCase) {
                                return (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Link
                                      to={`/support-cases?caseId=${existingCase.caseId}`}
                                      className="text-primary-600 hover:underline font-medium"
                                    >
                                      {t('pages:viewSupportCase', 'View support case')}
                                    </Link>
                                  </div>
                                );
                              }

                              if (state?.open) {
                                return (
                                  <div className="border border-gray-200 rounded-lg p-3 bg-white">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                      {t('pages:openSupportCaseHeading', 'Open Support Case')}
                                    </h4>
                                    {state.error && (
                                      <p role="alert" className="mb-2 text-xs text-red-600">
                                        {state.error}
                                      </p>
                                    )}
                                    <OpenSupportCaseForm
                                      bookingId={booking.bookingId}
                                      onSuccess={(caseId) => {
                                        navigate(`/support-cases?caseId=${caseId}`);
                                      }}
                                      onError={(msg) => {
                                        setCaseFormState(prev => ({
                                          ...prev,
                                          [booking.bookingId]: { open: true, error: msg },
                                        }));
                                      }}
                                      onCancel={() =>
                                        setCaseFormState(prev => ({
                                          ...prev,
                                          [booking.bookingId]: { open: false, error: null },
                                        }))
                                      }
                                    />
                                  </div>
                                );
                              }

                              return (
                                <div>
                                  <button
                                    type="button"
                                    className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
                                    onClick={() =>
                                      setCaseFormState(prev => ({
                                        ...prev,
                                        [booking.bookingId]: { open: true, error: null },
                                      }))
                                    }
                                  >
                                    {t('pages:openSupportCaseAction', 'Open Support Case')}
                                  </button>
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                   </Fragment>
                   ))}
                 </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Cancel dialog ─────────────────────────────────────────────── */}
      {cancelTarget && (
        <CancelBookingDialog
          bookingId={cancelTarget.bookingId}
          isOpen={true}
          onClose={() => setCancelTarget(null)}
          onSuccess={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
};

export default Bookings;
